/** De onde o livro reabre: espelho local ou servidor, o mais novo dos dois.
 *
 * O espelho local responde na hora e o servidor leva o tempo da rede da escola.
 * Esperar o servidor mostra a página 1 por um instante a cada abertura; usar só
 * o local perde a posição de quem leu no celular. Então: usa o local de
 * imediato e, quando o servidor chegar, corrige se ele for MAIS NOVO — mesmo
 * last-write-wins por `client_updated_at` que o backend aplica (N19).
 *
 * "Mais novo" e não "maior progresso": é decisão registrada da spec. O aluno
 * que volta para reler o capítulo 2 não quer ser jogado de volta ao capítulo 9.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { lerPosicaoLocal, type PosicaoLocal } from '@/services/localProgress'
import { fetchProgress } from '@/services/readerService'
import type { ReadingProgress } from '@/types/api'

export interface Retomada {
  page: number
  scrollPct: number
  /** `true` depois de o servidor responder (ou falhar) — a decisão é final. */
  resolvida: boolean
}

interface Opcoes {
  bookId: string | null
  habilitado?: boolean
  buscarServidor?: (bookId: string) => Promise<ReadingProgress>
  buscarLocal?: (bookId: string) => Promise<PosicaoLocal | null>
}

/** O carimbo `a` é mais novo que `b`? Sem `b`, qualquer `a` vence. */
function maisNovo(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a) return false
  if (!b) return true
  return new Date(a).getTime() > new Date(b).getTime()
}

export function useResumePosition({
  bookId,
  habilitado = true,
  buscarServidor = fetchProgress,
  buscarLocal = lerPosicaoLocal,
}: Opcoes): Retomada {
  // Estado CARIMBADO com o livro, como em `usePdfDocument`: um `setState` de
  // reset no início do efeito seria uma renderização em cascata, e um estado
  // sem carimbo entregaria a posição do livro ANTERIOR no instante em que o
  // aluno troca de material.
  const [lido, setLido] = useState<{ bookId: string; posicao: PosicaoLocal | null } | null>(
    null,
  )

  useEffect(() => {
    if (!bookId) return
    let vigente = true
    void buscarLocal(bookId).then((posicao) => {
      if (vigente) setLido({ bookId, posicao })
    })
    return () => {
      vigente = false
    }
  }, [bookId, buscarLocal])

  const lidoLocal = lido?.bookId === bookId
  const local = lidoLocal ? (lido?.posicao ?? null) : null

  const servidor = useQuery({
    queryKey: ['progress', bookId],
    queryFn: () => buscarServidor(bookId!),
    enabled: habilitado && bookId !== null,
    retry: false,
  })

  const doServidor = servidor.data
  const servidorVence =
    doServidor !== undefined &&
    (local === null || maisNovo(doServidor.client_updated_at, local.clientUpdatedAt))

  const escolhida = servidorVence
    ? { page: doServidor.page ?? 1, scrollPct: doServidor.scroll_pct ?? 0 }
    : { page: local?.page ?? 1, scrollPct: local?.scrollPct ?? 0 }

  return {
    page: escolhida.page > 0 ? escolhida.page : 1,
    scrollPct: escolhida.scrollPct,
    // `isFetched` cobre também o erro: um servidor fora do ar não pode deixar a
    // retomada pendente para sempre, senão a restauração nunca acontece.
    resolvida: lidoLocal && (!habilitado || bookId === null || servidor.isFetched),
  }
}
